import { useEffect } from "react";
import Tooltip from "@src/components/Tooltip";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { FEATURES } from "@src/constants";
import { usePlaylistTagsQuery } from "@src/queries/playlistTagsQueries";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import CategoryIcon from "./CategoryIcon";
import PlaylistSubtagsPicker, {
  usePlaylistTagSelection,
} from "./PlaylistSubtagsPicker";

type ConfirmSubmitModalProps = {
  onConfirm: (
    playlistTagsSelected: string[],
    /** `undefined` while `FEATURES.PLAYLIST_SUBTAGS_ENABLED` is off [ROVR-4246]. */
    playlistSubtagsSelected: string[] | undefined
  ) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
};

const ConfirmSubmitModal: React.FC<ConfirmSubmitModalProps> = ({
  onConfirm,
  onClose,
  isOpen,
  loading,
}) => {
  const { data: categories, isFetching } = usePlaylistTagsQuery({
    enabled: isOpen,
    withSubtags: true,
  });
  const selection = usePlaylistTagSelection(categories);
  const {
    selectedCategories,
    selectedSubtagIds,
    onSelectCategory,
    reset: resetSelection,
  } = selection;

  /**
   * Every selection is scoped to one visit to the popup. This modal is mounted
   * for the life of the page, so without a reset a curator who left it by the
   * overlay or the X — neither of which ran the Cancel handler — came back to
   * tags and subtags still picked from last time. A subtag picked under a
   * collapsed section is invisible, so those rode along into the next submit
   * without the curator ever seeing them [ROVR-3982].
   */
  useEffect(() => {
    if (!isOpen) return;
    resetSelection();
  }, [isOpen, resetSelection]);

  return (
    <UIModal
      modalStyles={{
        // 538px is the popup in Figma; the tag icons and the subtag pills both
        // wrap into fewer rows at that width, which is half of what keeps the
        // modal short enough to fit [ROVR-3982].
        width: 538,
        maxWidth: "calc(100vw - 32px)",
      }}
      onClose={onClose}
      isOpen={isOpen}
      hideCloseButton
      actions={[
        <UIButton key="cancel" onClick={onClose} variant="secondaryOutline">
          Cancel
        </UIButton>,
        <UIButton
          key="delete"
          onClick={() =>
            onConfirm(
              selectedCategories,
              FEATURES.PLAYLIST_SUBTAGS_ENABLED ? selectedSubtagIds : undefined
            )
          }
          variant="primary"
          disabled={!selectedCategories.length || isFetching || loading}
        >
          Submit
        </UIButton>,
      ]}
      headerStyles={{
        textAlign: "center",
      }}
      title="You’re about to submit your playlist."
    >
      <ModalBody data-testid="confirm-submit-body">
        <p
          style={{
            width: "100%",
            textAlign: "center",
            color: theme.colors.gray,
          }}
        >
          Select at least one category that best describes the vibe of your
          playlist
        </p>

        <CategoriesContainer>
          {categories?.map((category) => (
            <Tooltip content={category.description} key={category.documentId}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  userSelect: "none",
                }}
              >
                <CategoryButton
                  aria-selected={selectedCategories.includes(
                    category.documentId
                  )}
                  key={category.documentId}
                  onClick={() => onSelectCategory(category.documentId)}
                  $active={selectedCategories.includes(category.documentId)}
                  className={
                    selectedCategories.includes(category.documentId)
                      ? "category-active"
                      : ""
                  }
                >
                  <CategoryIcon
                    url={category.icon?.url}
                    alt={
                      category.icon?.alternativeText ||
                      category.label ||
                      "Category icon"
                    }
                  />
                </CategoryButton>
                <span
                  style={{
                    fontSize: "12px",
                    textTransform: "uppercase",
                    textAlign: "center",
                    color: theme.colors.white,
                  }}
                >
                  {category.label}
                </span>
              </div>
            </Tooltip>
          ))}
          {(isFetching || loading) && <Spinner small />}
        </CategoriesContainer>

        <PlaylistSubtagsPicker
          categoriesWithSubtags={selection.categoriesWithSubtags}
          expandedCategoryId={selection.expandedCategoryId}
          selectedSubtags={selection.selectedSubtags}
          onSelectSubtag={selection.onSelectSubtag}
          onExpandCategory={selection.onExpandCategory}
        />
      </ModalBody>

      <p
        style={{
          width: "100%",
          textAlign: "center",
          color: theme.colors.primary,
        }}
      >
        This action is final.
      </p>
    </UIModal>
  );
};

/**
 * Everything above the footer scrolls [ROVR-3982]. The modal is centred by a
 * fixed overlay, so before this the tag list plus an open subtags section could
 * push the "This action is final." line and the Cancel/Submit row past the
 * bottom of the viewport, where they were cut in half or gone entirely with no
 * way to scroll to them.
 *
 * The subtrahend is the chrome around this box — 40px of modal padding, the
 * two-line title and its margin, this box's own margin, the final-action line
 * and the button row — rounded up so the footer always clears. The floor keeps
 * a usable body on a short viewport, where the modal may overflow but the page
 * behind it is unusable at that height anyway.
 */
const ModalBody = styled.div`
  max-height: max(220px, calc(100vh - 280px));
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Keeps the pills off the scrollbar once the body starts scrolling. */
  padding-right: 4px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  min-height: 50px;
  padding: 10px;
`;
const CategoryButton = styled.button<{ $active: boolean }>`
  user-drag: none;
  -webkit-user-drag: none;
  -moz-user-select: none;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${(props): string =>
    props.$active ? theme.colors.primary : "transparent"};
  height: 44px;
  width: 44px;
  object-fit: contain;
  border: none;
  border-radius: 50%;
  user-select: none;
  /*
   * The icon is a mask painted with this colour (see CategoryIcon), so every
   * tag reads the same whatever colour its asset was uploaded in.
   */
  color: ${(props): string =>
    props.$active ? theme.colors.black : theme.colors.primary};
  transition:
    background 0.2s ease-in-out,
    color 0.2s ease-in-out,
    border 0.2s ease-in-out;
`;

export default ConfirmSubmitModal;
